import callCmAPI from 'services/helpers/call-cm-api'
import {
  CustomerGroupInUseErrorT,
  DeleteCustomerGroupSuccessT,
  SuccessResponseT,
} from 'types/global'

/**
 * Narrowed return: either a clean success envelope (200) or the
 * 409 CustomerGroupInUse body. Other errors (404, 500, network)
 * still throw, so React Query surfaces them via `onError`.
 */
export type DeleteCustomerGroupResult =
  | SuccessResponseT<DeleteCustomerGroupSuccessT>
  | CustomerGroupInUseErrorT

/**
 * Deletes a customer group.
 *
 * The CMAPI returns three meaningful statuses:
 *  - 200: success body `{ success: true, data: { id } }`
 *  - 404: not found — surfaced through the global error handler
 *  - 409: the group is still referenced by at least one non-deleted
 *    banner. The body is `CustomerGroupInUseErrorT`, with the
 *    referencing banner IDs that CMD must surface to the operator.
 *
 * We unwrap the 409 here (axios throws on 4xx by default) and return
 * the in-use body so the mutation hook can render a notistack
 * snackbar listing the banner IDs without throwing through React
 * Query's onError pipeline.
 */
export const deleteCustomerGroup = async (
  id: string,
): Promise<DeleteCustomerGroupResult> => {
  try {
    const res = await callCmAPI(`/customer-group/${id}`, {
      method: 'delete',
    })
    return res.data as SuccessResponseT<DeleteCustomerGroupSuccessT>
  } catch (err: unknown) {
    const axiosErr = err as {
      response?: { status?: number; data?: unknown }
    }
    const status = axiosErr?.response?.status
    const body = axiosErr?.response?.data as
      | CustomerGroupInUseErrorT
      | undefined

    if (status === 409 && body?.error?.name === 'CustomerGroupInUse') {
      return body
    }
    throw err
  }
}
